import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FiCheck,
  FiChevronDown,
  FiHeart,
  FiPlus,
  FiTruck,
  FiX,
} from "react-icons/fi";
import productData from "../Datas/productData";
import Footer from "./Footer";
import "../css/productdetails.scss";
import { CartContext } from "./CartContext";
import Header from "./Header";
import siteConfig, { formatPrice } from "../config/siteConfig";

const detailSections = {
  Details: (product) => (
    <>
      <p>{product.description}</p>
      <ul>
        {product.features.map((feature) => (
          <li key={feature}>{feature}</li>
        ))}
      </ul>
    </>
  ),
  "Care instructions": () => (
    <p>
      Wear often and wash sparingly. Turn inside out, use a gentle cold cycle,
      and hang dry. Skip fabric softener and never tumble dry.
    </p>
  ),
  Transparency: (product) => (
    <dl>
      <div>
        <dt>Material</dt>
        <dd>{product.attributes.material}</dd>
      </div>
      <div>
        <dt>Fabric weight</dt>
        <dd>{product.attributes.weight}</dd>
      </div>
      <div>
        <dt>Made in</dt>
        <dd>{product.attributes.madeIn}</dd>
      </div>
    </dl>
  ),
  "Delivery & returns": () => (
    <p>
      Standard tracked shipping is {formatPrice(siteConfig.shippingFee)}. Eligible
      unworn items may be registered for return within {siteConfig.returnWindowDays} days.
    </p>
  ),
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, wishlist, toggleWishlist } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState(product?.size?.[0] || "");
  const [selectedColor, setSelectedColor] = useState(
    product?.colors?.[0] || ""
  );
  const [selectedImage, setSelectedImage] = useState(0);
  const [infoOpen, setInfoOpen] = useState(null);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setSelectedSize(product?.size?.[0] || "");
    setSelectedColor(product?.colors?.[0] || "");
    setSelectedImage(0);
    setAdded(false);
    window.scrollTo(0, 0);
  }, [product]);

  useEffect(() => {
    document.body.style.overflow = infoOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [infoOpen]);

  if (!product) {
    return (
      <div className="product-detail">
        <Header />
        <main className="product-detail__not-found">
          <div>
            <span className="eyebrow">404</span>
            <h1>Product not found</h1>
            <Link className="app-button" to="/shop">
              Back to shop
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const saved = wishlist.includes(product.id);

  const handleAddToCart = () => {
    addToCart(product.id, 1, selectedSize, selectedColor);
    setAdded(true);
    window.setTimeout(() => setAdded(false), 1800);
  };

  const relatedProducts = productData
    .filter(
      (item) =>
        item.id !== product.id &&
        (item.category === product.category ||
          item.attributes.fit === product.attributes.fit)
    )
    .slice(0, 4);

  return (
    <div className="product-detail">
      <Header />
      <main className="product-detail__layout">
        <section className="product-detail__summary">
          <div className="product-detail__summary-inner">
            <nav
              className="product-detail__breadcrumbs"
              aria-label="Breadcrumb"
            >
              <Link to="/shop">Shop</Link>
              <span>/</span>
              <Link to={`/shop?category=${product.category}`}>
                {product.category}
              </Link>
            </nav>
            <div className="product-detail__title-row">
              <h1>{product.name}</h1>
              <button
                onClick={() => toggleWishlist(product.id)}
                aria-label={
                  saved ? "Remove from favorites" : "Add to favorites"
                }
                aria-pressed={saved}
              >
                <FiHeart className={saved ? "is-saved" : ""} />
              </button>
            </div>
            <p className="product-detail__price">
              {formatPrice(product.price)}
            </p>
            <p className="product-detail__intro">{product.description}</p>
            <div className="product-detail__facts">
              <span>{product.attributes.fit} fit</span>
              <span>{product.attributes.waist} waist</span>
              <span>{product.attributes.leg} leg</span>
            </div>
            <Link className="product-detail__review" to="#details">
              ★★★★★ <span>Built for years of wear</span>
            </Link>
          </div>
        </section>

        <section
          className="product-detail__gallery"
          aria-label="Product images"
        >
          <div className="product-detail__mobile-slider">
            {product.images.map((image, index) => (
              <button
                key={image}
                onClick={() => setSelectedImage(index)}
                className={selectedImage === index ? "is-active" : ""}
                aria-label={`Show image ${index + 1}`}
              >
                <img src={image} alt={`${product.name}, view ${index + 1}`} />
              </button>
            ))}
          </div>
          {product.images.map((image, index) => (
            <figure key={image} className="product-detail__image">
              <img
                src={image}
                alt={`${product.name}, ${
                  index === 0 ? "full product view" : "detail view"
                }`}
                loading={index === 0 ? "eager" : "lazy"}
              />
            </figure>
          ))}
          <div className="product-detail__progress">
            <span
              style={{
                width: `${100 / product.images.length}%`,
                transform: `translateX(${selectedImage * 100}%)`,
              }}
            />
          </div>
        </section>

        <aside className="product-detail__buy">
          <div className="product-detail__buy-inner">
            <div className="product-detail__color">
              <div>
                <span>Color</span>
                <strong>{selectedColor}</strong>
              </div>
              <button
                className="product-detail__swatch"
                style={{ "--swatch": product.attributes.colorHex }}
                aria-label={selectedColor}
              />
            </div>

            <div className="product-detail__sizes">
              <div className="product-detail__option-header">
                <span>
                  {product.category === "Jeans" ? "Waist / length" : "Size"}
                </span>
              </div>
              <div className="product-detail__size-grid">
                {product.size.map((size) => (
                  <button
                    key={size}
                    className={selectedSize === size ? "is-selected" : ""}
                    onClick={() => setSelectedSize(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            <button
              className={`product-detail__add ${added ? "is-added" : ""}`}
              onClick={handleAddToCart}
            >
              {added ? (
                <>
                  <FiCheck /> Added to bag
                </>
              ) : (
                <>
                  Add to bag <span>{formatPrice(product.price)}</span>
                </>
              )}
            </button>
            {added && (
              <button
                className="product-detail__checkout-link"
                onClick={() => navigate("/cartpage")}
              >
                View bag and checkout
              </button>
            )}

            <div className="product-detail__service-links">
              <p>
                <FiTruck /> {formatPrice(siteConfig.shippingFee)} tracked shipping · {siteConfig.returnWindowDays}-day returns
              </p>
            </div>

            <div className="product-detail__info-buttons" id="details">
              {Object.keys(detailSections).map((section) => (
                <button key={section} onClick={() => setInfoOpen(section)}>
                  <span>{section}</span>
                  <FiPlus />
                </button>
              ))}
            </div>
          </div>
        </aside>
      </main>

      <section className="product-detail__story">
        <div>
          <span className="eyebrow">A better fade</span>
          <h2>Let wear do the washing.</h2>
        </div>
        <p>
          The longer you wait before the first wash, the more personal the marks
          become. When the time comes, wash cool and air dry.
        </p>
      </section>

      <section className="product-detail__related">
        <header>
          <h2>You may also like</h2>
          <Link to="/shop">View all</Link>
        </header>
        <div>
          {relatedProducts.map((item) => (
            <Link key={item.id} to={`/shopDetails/${item.id}`}>
              <img src={item.image} alt={item.name} loading="lazy" />
              <span>{item.name}</span>
              <small>{formatPrice(item.price)}</small>
            </Link>
          ))}
        </div>
      </section>

      <div
        className={`product-detail__drawer-backdrop ${
          infoOpen ? "is-open" : ""
        }`}
        onClick={() => setInfoOpen(null)}
        aria-hidden="true"
      />
      <aside
        className={`product-detail__drawer ${infoOpen ? "is-open" : ""}`}
        aria-hidden={!infoOpen}
      >
        <header>
          <h2>{infoOpen}</h2>
          <button
            onClick={() => setInfoOpen(null)}
            aria-label="Close product information"
          >
            <FiX />
          </button>
        </header>
        <div>{infoOpen && detailSections[infoOpen](product)}</div>
        <button
          className="product-detail__drawer-close"
          onClick={() => setInfoOpen(null)}
        >
          Close <FiChevronDown />
        </button>
      </aside>

      <Footer />
    </div>
  );
};

export default ProductDetail;
